import { Injectable } from '@angular/core';
import {
  HttpBackend,
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpErrorResponse,
  HttpClient,
} from '@angular/common/http';
import { AuthService } from 'src/app/authentication/services/auth.service';
import { Router } from '@angular/router';
import { NEVER, Observable, throwError, switchMap, BehaviorSubject, filter, take } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ApiCallMethodsService } from '../services/api-call-methods.service';
import { apiRoutes } from 'src/app/constants/apiRoutes';
import { environment } from 'src/environments/environment';
import Cookies from 'js-cookie';

@Injectable()
export class HttpTokenInterceptor implements HttpInterceptor {

  /** Bypass client – skips all interceptors so the refresh-token call cannot
   *  deadlock by re-entering this interceptor when it receives a 401. */
  private bypassHttp: HttpClient;

  constructor(
    private authService: AuthService,
    private router: Router,
    private modalService: NgbModal,
    private apiCallMethodsService: ApiCallMethodsService,
    private http: HttpClient,
    handler: HttpBackend
  ) {
    this.bypassHttp = new HttpClient(handler);
  }

  private isRefreshing = false;
  private refreshTokenSubject = new BehaviorSubject<string | null>(null);


  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (this.authService.isAuthenticated()) {
      request = this.addToken(request, this.authService.getToken());
    }
    else {
      request = this.addToken(request, null);
    }

    if (request.url.includes('admin/org-list')) {
      const loginTypeCookie = Cookies.get('cb_login_type');
      const sessionDetail = this.authService.getSessionDetail();
      const user = sessionDetail?.user || {};
      let loginTypeValue = '';

      if (loginTypeCookie === 'phone') {
        loginTypeValue = (user.phone || '').replace(/\D/g, '').slice(-10);
      } else {
        loginTypeValue = user.email || '';
      }

      let cleanedUrl = request.url;
      if (request.url.includes('admin/org-list/')) {
        cleanedUrl = request.url.split('/admin/org-list/')[0] + '/admin/org-list';
      }

      request = request.clone({
        url: cleanedUrl,
        setParams: {
          login_type: loginTypeValue
        }
      });
    }

    return next.handle(request).pipe(
      catchError((error) => {
        if (error instanceof HttpErrorResponse && error.status === 401) {
          return this.handle401Error(request, next, error);
        } else {
          return throwError(error);
        }
      })
    );


  }

  private addToken(
    request: HttpRequest<any>,
    token: string = null,
  ): HttpRequest<any> {
    let headers = {};

    if (token !== null) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    return request.clone({
      setHeaders: headers,
    });
  }

  private handle401Error(
    request: HttpRequest<any>,
    next: HttpHandler,
    error: any
  ): Observable<any> {

    const sessionDetail = this.authService.getSessionDetail();
    const ssoSession = Cookies.get("sso_session");

    if (!sessionDetail) {
      return throwError(() => error);
    }

    // 🔁 IF refresh already in progress → WAIT
    if (this.isRefreshing) {
      return this.refreshTokenSubject.pipe(
        filter(token => token !== null),
        take(1),
        switchMap(token =>
          next.handle(this.addToken(request, token!))
        )
      );
    }

    // START refresh flow
    this.isRefreshing = true;
    this.refreshTokenSubject.next(null);

    const refreshTokenPayload = {
      sessionId: ssoSession,
      product_client_id: "cb-sales"
    };

    const apiUrl = `${environment.ssoApiUrl}sso/refresh-token`;

    // Use bypassHttp so the refresh-token call is never caught by this interceptor.
    // If this.http were used here and refresh-token also returned 401, the interceptor
    // would re-enter handle401Error with isRefreshing=true, returning a permanently-
    // hanging observable (refreshTokenSubject only emits null which is filtered out).
    return this.bypassHttp.post(apiUrl, refreshTokenPayload).pipe(
      switchMap((response: any) => {
        const newAccessToken = response?.data?.accessToken;

        if (!newAccessToken) {
          throw new Error('No access token from refresh');
        }

        // ✅ Save new token
        const newSessionDetail = {
          ...sessionDetail,
          token: {
            token: newAccessToken,
            idToken: response.data.idToken
          }
        };

        this.authService.setToken(newSessionDetail);

        // 🔔 Notify waiting requests
        this.isRefreshing = false;
        this.refreshTokenSubject.next(newAccessToken);

        // 🔁 Retry original request
        return next.handle(this.addToken(request, newAccessToken));
      }),
      catchError(err => {
        // ❌ Refresh failed → logout everyone
        this.isRefreshing = false;
        this.refreshTokenSubject.next(null);
        this.authService.globalLogout();

        // this.forceLogout();
        return throwError(() => err);
      })
    );
  }


  // private handle401Error(request: HttpRequest<any>, next: HttpHandler, error: any): Observable<any> {
  //   const sessionDetail = this.authService.getSessionDetail();
  //   const ssoSession = Cookies.get("sso_session");
  //   if (sessionDetail) {
  //     const refreshTokenPayload = {
  //       // username: sessionDetail?.user?.cognito_username,
  //       // refreshToken: sessionDetail?.token?.refreshToken,
  //       sessionId: ssoSession || '1bcce538-6a74-4a15-a3d0-d67e1e36ac6f',
  //       product_client_id: "cb-sales"
  //     };

  //     // 🚫 Do NOT log out here — wait until refresh fails

  //     let apiUrl = `${environment.ssoApiUrl}sso/refresh-token`;

  //     return this.http.post(apiUrl, refreshTokenPayload).pipe(
  //       switchMap((response: any) => {
  //         if (response?.data?.accessToken) {
  //           // ✅ update session with new tokens
  //           const newSessionDetail = {
  //             ...sessionDetail,
  //             token: {
  //               token: response.data.accessToken,
  //               idToken: response.data.idToken,
  //               // refreshToken: response.token.refreshToken,
  //             },
  //           };

  //           this.authService.setToken(newSessionDetail);
  //           console.log(newSessionDetail);

  //           console.log('Refresh Success');


  //           // 🔄 retry original request with fresh token
  //           return next.handle(this.addToken(request, response?.data?.accessToken));
  //         } else {
  //           // no token returned → force logout
  //           console.log('Refresh Failed ', response);

  //           // this.forceLogout();
  //           // this.authService.globalLogout();
  //           return throwError(() => error);
  //         }
  //       }),
  //       catchError((err) => {
  //         // refresh failed → logout
  //         console.log('Refresh Failed ', err);

  //         // this.forceLogout();
  //         // this.authService.globalLogout();
  //         return throwError(() => err);
  //       })
  //     );
  //   } else {
  //     // no session → logout
  //     console.log('No Session ');

  //     // this.forceLogout();
  //     // this.authService.globalLogout();
  //     return throwError(() => error);
  //   }
  // }
}
